/**
 * Key generation and derivation for SkySend.
 *
 * Secret (32 bytes) -> HKDF-SHA256 -> fileKey, metaKey, authKey
 *
 * Security notes:
 * - The 32-byte secret is generated via crypto.getRandomValues (CSPRNG)
 * - HKDF uses distinct info strings per derived key to ensure domain separation
 * - The salt is bound to each upload and stored alongside the ciphertext
 * - fileKey and metaKey are AES-256-GCM keys
 * - authKey is used with HMAC-SHA256 to produce an auth token
 */
import { asBytes, randomBytes } from "./util.js";
/** Length of the master secret in bytes (256 bits). */
export const SECRET_LENGTH = 32;
/**
 * Length of the salt used in HKDF derivation for new uploads (32 bytes = SHA-256 output per RFC 5869).
 * Legacy uploads with 16-byte salts are still accepted by deriveKeys() for backward compatibility.
 */
export const SALT_LENGTH = 32;
/** HKDF info strings for domain separation. */
const HKDF_INFO_FILE = "skysend-file-encryption";
const HKDF_INFO_META = "skysend-metadata";
const HKDF_INFO_AUTH = "skysend-authentication";
/** Generate a new 256-bit cryptographic secret. */
export function generateSecret() {
    return randomBytes(SECRET_LENGTH);
}
/** Generate a new random salt for key derivation. */
export function generateSalt() {
    return randomBytes(SALT_LENGTH);
}
/**
 * Import the raw secret as an HKDF base key.
 *
 * The secret is imported as non-extractable to prevent
 * accidental exposure via `exportKey()`.
 */
async function importHkdfKey(secret) {
    if (secret.length !== SECRET_LENGTH) {
        throw new Error(`Secret must be exactly ${SECRET_LENGTH} bytes`);
    }
    return crypto.subtle.importKey("raw", asBytes(secret), "HKDF", false, ["deriveKey", "deriveBits"]);
}
/**
 * Derive an AES-256-GCM key from the HKDF base key.
 */
async function deriveAesGcmKey(baseKey, salt, info) {
    const encoder = new TextEncoder();
    return crypto.subtle.deriveKey({
        name: "HKDF",
        hash: "SHA-256",
        salt: asBytes(salt),
        info: asBytes(encoder.encode(info)),
    }, baseKey, { name: "AES-GCM", length: 256 }, false, // non-extractable
    ["encrypt", "decrypt"]);
}
/**
 * Derive an HMAC-SHA256 key from the HKDF base key.
 */
async function deriveHmacKey(baseKey, salt, info) {
    const encoder = new TextEncoder();
    return crypto.subtle.deriveKey({
        name: "HKDF",
        hash: "SHA-256",
        salt: asBytes(salt),
        info: asBytes(encoder.encode(info)),
    }, baseKey, { name: "HMAC", hash: "SHA-256", length: 256 }, false, // non-extractable
    ["sign", "verify"]);
}
/**
 * Derive all three keys (fileKey, metaKey, authKey) from a secret and salt.
 *
 * Each key uses a distinct HKDF info string for cryptographic domain separation.
 * All derived keys are non-extractable - they can only be used for their
 * intended operations (encrypt/decrypt or sign/verify).
 */
export async function deriveKeys(secret, salt) {
    // Accept both 32 bytes (new uploads) and 16 bytes (legacy uploads).
    // TODO: Remove the 16-byte branch once all existing uploads with legacy salts have expired.
    //       After removing, update this check to: if (salt.length !== SALT_LENGTH)
    if (salt.length !== 32 && salt.length !== 16) {
        throw new Error(`Salt must be 16 or 32 bytes, got ${salt.length}`);
    }
    const baseKey = await importHkdfKey(secret);
    const [fileKey, metaKey, authKey] = await Promise.all([
        deriveAesGcmKey(baseKey, salt, HKDF_INFO_FILE),
        deriveAesGcmKey(baseKey, salt, HKDF_INFO_META),
        deriveHmacKey(baseKey, salt, HKDF_INFO_AUTH),
    ]);
    return { fileKey, metaKey, authKey };
}
/**
 * Compute an auth token from the authKey.
 *
 * The auth token is HMAC-SHA256(authKey, "skysend-auth-token").
 * This produces a deterministic 32-byte token that the server can
 * verify without knowing the secret.
 */
export async function computeAuthToken(authKey) {
    const encoder = new TextEncoder();
    const data = encoder.encode("skysend-auth-token");
    const signature = await crypto.subtle.sign("HMAC", authKey, data);
    return new Uint8Array(signature);
}
/**
 * Compute an owner token from the secret.
 *
 * The owner token is derived independently using HKDF with a distinct info string.
 * This allows the uploader to prove ownership without exposing the secret.
 * The server stores this token to authorize deletion requests.
 */
export async function computeOwnerToken(secret, salt) {
    if (secret.length !== SECRET_LENGTH) {
        throw new Error(`Secret must be exactly ${SECRET_LENGTH} bytes`);
    }
    const baseKey = await importHkdfKey(secret);
    const encoder = new TextEncoder();
    const bits = await crypto.subtle.deriveBits({
        name: "HKDF",
        hash: "SHA-256",
        salt: asBytes(salt),
        info: asBytes(encoder.encode("skysend-owner-token")),
    }, baseKey, 256);
    return new Uint8Array(bits);
}
//# sourceMappingURL=keychain.js.map